import Phaser from "phaser";
import { getAgritechText, getAgritechLanguage } from "../utils/agritechText";

export default class ResultScene extends Phaser.Scene {
  constructor() {
    super("ResultScene");
  }

  init(data) {
    this.score = data.score || 0;
    this.blueCount = data.blueCount || 0;
    this.greenCount = data.greenCount || 0;
    this.starCount = data.starCount || 0;
    this.blueScore = data.blueScore || 0;
    this.greenScore = data.greenScore || 0;
    this.starScore = data.starScore || 0;
  }

  showReturnButton(finalPlantX, finalPlantY) {
    const leftPanel = document.getElementById("left-ui-panel");

    if (leftPanel) {
      leftPanel.innerHTML = `
  <div class="h-full flex flex-col text-white">
   <div class="developer-logo-content">
        <p class="developer-logo-label">
          Desarrollado por:
        </p>

        <img
          src="/assets/CES.png"
          alt="CES University"
          class="institution-logo"
        />

        <p class="developer-logo-label">
          En el marco de:
        </p>

        <img
          src="/assets/futumed.png"
          alt="Futumed"
          class="futumed-logo"
        />
      </div>

    <div class="mt-auto flex justify-center pb-10">
  <button
    id="return-title-button"
    class="rounded-xl border border-[#769b55] bg-[#0b1510] px-5 py-4 text-sm font-bold text-white hover:bg-[#18291f] hover:text-[#d9f99d] transition cursor-pointer"
  >
    ${getAgritechLanguage() === "es" ? "REGRESAR" : "RETURN"}
  </button>
</div>
  </div>
`;
    }

    const button = document.getElementById("return-title-button");

    if (button) {
      button.addEventListener("click", () => {
        button.disabled = true;
        button.className =
          "rounded-xl border border-[#405747] bg-[#18231D] px-5 py-4 text-sm font-bold text-[#8d9b90]";

        const blackOverlay = this.add
          .rectangle(400, 300, 800, 600, 0x000000, 1)
          .setDepth(9999);

        const holeGraphics = this.make.graphics({ x: 0, y: 0, add: false });
        const holeMask = holeGraphics.createGeometryMask();
        holeMask.setInvertAlpha(true);

        blackOverlay.setMask(holeMask);

        const closing = { radius: 1000 };

        this.tweens.add({
          targets: closing,
          radius: 1,
          duration: 1200,
          ease: "sine.inOut",
          onUpdate: () => {
            holeGraphics.clear();
            holeGraphics.fillStyle(0xffffff, 1);
            holeGraphics.fillCircle(finalPlantX, finalPlantY, closing.radius);
          },
          onComplete: () => {
            blackOverlay.clearMask();
            holeGraphics.destroy();

            if (this.resultsMusic) {
              this.resultsMusic.stop();
            }

            this.input.keyboard.clearCaptures();
            this.input.keyboard.resetKeys();

            localStorage.setItem("agritech-language", "es");
            window.dispatchEvent(new Event("agritech-language-change"));

            this.scene.start("TitleScene", {
              revealFrom: {
                x: finalPlantX,
                y: finalPlantY,
              },
            });
          },
        });
      });
    }
  }

  showMinorIdReminder() {
    const player = window.currentBiorevPlayer;
    const participantId = player?.participantId?.trim();

    if (player?.isAdult !== false || !participantId || this.idReminderObjects) {
      return;
    }

    const title = this.lang === "es" ? "RECUERDA TU ID" : "REMEMBER YOUR ID";
    const message =
      this.lang === "es"
        ? "Este es tu ID de jugador. Anótalo para que puedas seguir participando con tu mismo perfil."
        : "This is your player ID. Write it down so you can keep participating with the same profile.";

    const overlay = this.add
      .rectangle(400, 300, 800, 600, 0x000000, 0.55)
      .setDepth(1000)
      .setInteractive();
    const panel = this.add
      .rectangle(400, 300, 540, 250, 0x18231d, 1)
      .setStrokeStyle(3, 0x769b55)
      .setDepth(1001);
    const titleText = this.add
      .text(400, 220, title, {
        fontFamily: '"Russo One", sans-serif',
        fontSize: "28px",
        color: "#ecfccb",
        align: "center",
      })
      .setOrigin(0.5)
      .setDepth(1002);
    const messageText = this.add
      .text(400, 275, message, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "17px",
        color: "#f7faf4",
        align: "center",
        wordWrap: { width: 440 },
        lineSpacing: 4,
      })
      .setOrigin(0.5)
      .setDepth(1002);
    const idText = this.add
      .text(400, 355, participantId, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: participantId.length > 20 ? "22px" : "32px",
        fontStyle: "bold",
        color: "#d9f99d",
        align: "center",
        wordWrap: { width: 440, useAdvancedWrap: true },
      })
      .setOrigin(0.5)
      .setDepth(1002);
    const closeButton = this.add
      .circle(642, 195, 18, 0x0b1510, 1)
      .setStrokeStyle(2, 0x769b55)
      .setDepth(1003)
      .setInteractive({ useHandCursor: true });
    const closeText = this.add
      .text(642, 193, "X", {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "28px",
        fontStyle: "bold",
        color: "#ecfccb",
      })
      .setOrigin(0.5)
      .setDepth(1004);

    this.idReminderObjects = [
      overlay,
      panel,
      titleText,
      messageText,
      idText,
      closeButton,
      closeText,
    ];

    closeButton.on("pointerdown", () => {
      this.idReminderObjects.forEach((object) => object.destroy());
      this.idReminderObjects = null;
    });
  }

  preload() {
    this.load.image("walk0", "/assets/movement/walk0.png");
    this.load.image("walk1", "/assets/movement/walk1.png");
    this.load.image("walk2", "/assets/movement/walk2.png");
    this.load.image("walk3", "/assets/movement/walk3.png");
    this.load.image("walk4", "/assets/movement/walk4.png");
    this.load.image("walk5", "/assets/movement/walk5.png");
    this.load.image("walk6", "/assets/movement/walk6.png");
    this.load.image("walk7", "/assets/movement/walk7.png");
    this.load.image("kneel", "/assets/movement/kneel.png");
    this.load.image("final", "/assets/movement/final.png");
    this.load.image("plant", "/assets/plant.png");
    this.load.image("flower", "/assets/flower.png");
    this.load.audio("plantBit", "/assets/plantBit.wav");
    this.load.audio("resultsMusic", "/assets/Results.mp3");
    this.load.audio("complete", "/assets/complete.wav");
  }

  create() {
    this.t = getAgritechText();
    this.lang = getAgritechLanguage();

    window.dispatchEvent(
      new CustomEvent("biorev-mobile-ui", {
        detail: {
          mode: "result",
          hideRightPanel: false,
          visibleControls: [],
          powerStates: {
            one: false,
            two: false,
            three: false,
          },
        },
      }),
    );

    const rightPanel = document.getElementById("right-ui-panel");
    const gameplayContent = document.getElementById(
      "right-panel-gameplay-content",
    );
    const gameplayTimer = document.getElementById("gameplay-timer");
    const gameplayPowers = document.getElementById("gameplay-powers");
    const resultsCounters = document.getElementById("results-counters");
    const resultsTotal = document.getElementById("results-total");

    if (rightPanel) rightPanel.style.visibility = "visible";
    if (gameplayContent) gameplayContent.style.display = "";
    if (gameplayTimer) gameplayTimer.style.display = "none";
    if (gameplayPowers) gameplayPowers.style.display = "none";
    if (resultsCounters) resultsCounters.style.display = "";
    if (resultsTotal) resultsTotal.style.display = "none";

    const blueCountEl = document.getElementById("ui-blue-count");
    const greenCountEl = document.getElementById("ui-green-count");
    const starCountEl = document.getElementById("ui-star-count");
    const blueScoreEl = document.getElementById("ui-blue-score");
    const greenScoreEl = document.getElementById("ui-green-score");
    const starScoreEl = document.getElementById("ui-star-score");
    const scoreEl = document.getElementById("ui-score");

    if (blueCountEl) blueCountEl.textContent = this.blueCount;
    if (greenCountEl) greenCountEl.textContent = this.greenCount;
    if (starCountEl) starCountEl.textContent = this.starCount;
    if (blueScoreEl) blueScoreEl.textContent = this.blueScore;
    if (greenScoreEl) greenScoreEl.textContent = this.greenScore;
    if (starScoreEl) starScoreEl.textContent = this.starScore;
    if (scoreEl) scoreEl.textContent = this.score;

    const controlsUI = document.getElementById("main-scene-controls-ui");
    if (controlsUI) controlsUI.remove();
    this.cameras.main.setBackgroundColor("#000000");

    this.resultsMusic = this.sound.add("resultsMusic", {
      loop: true,
      volume: Number(localStorage.getItem("agritech-music-volume") ?? "0.4"),
    });

    this.resultsMusic.play();
    /*
        Credits to place elsewhere later:
        Game Level Music: Maksym Malko from Pixabay
        Correct SFX: KauÃ£ Silbershlach Parodes from Pixabay
        Incorrect SFX: freesound_community from Pixabay
        Power Up 1 SFX: Eschwabe3 (freesound.org/s/460131)
        Power Up 2 SFX: Eschwabe3 (freesound.org/s/460132)
        Apply SFX: Apply - Rpg by colorsCrimsonTears (freesound.org/s/577961)
        Plant Bit SFX: LevelUp.wav by Kenneth_Cooney (freesound.org/s/609335)
        Results Music: Day in the Park by SterlingRay (freesound.org/s/718481)
        8bit_status_137.wav by Soundholder (freesound.org/s/425356)
        Mud Splash: adiantheman (freesound.org/s/841538)
        Water Splash: lwdickens (freesound.org/s/269003)
        Thunderclap SFX: seth-m (pixabay.com/sound-effects/nature-big-thunder-clap-99753/)
        Tap SFX: Lesiakower from Pixabay
        Selected SFX: KauÃ£ Silbershlach Parodes from Pixabay
        */

    if (this.anims.exists("resultWalk")) {
      this.anims.remove("resultWalk");
    }

    this.anims.create({
      key: "resultWalk",
      frames: [
        { key: "walk0" },
        { key: "walk1" },
        { key: "walk2" },
        { key: "walk3" },
        { key: "walk4" },
        { key: "walk5" },
        { key: "walk6" },
        { key: "walk7" },
      ],
      frameRate: 8,
      repeat: -1,
    });

    const resultX = 350;
    const resultY = 445;
    const resultCharacterScale = 1;

    const character = this.add
      .sprite(-80, resultY, "walk0")
      .setScale(resultCharacterScale)
      .setFlipX(true);

    character.anims.play("resultWalk", true);

    this.tweens.add({
      targets: character,
      x: resultX,
      duration: 2200,
      ease: "linear",
      onComplete: () => {
        character.anims.stop();
        character.setTexture("walk0");

        this.time.delayedCall(1000, () => {
          character.setTexture("kneel");

          this.time.delayedCall(500, () => {
            character.setTexture("final");

            const plantBits = Math.ceil(this.score / 2500);
            const plantX = character.x + 38;
            const plantBaseY = character.y + 6;
            const plantSpacing = 40;

            for (let i = 0; i < plantBits; i++) {
              this.time.delayedCall(1000 * (i + 1), () => {
                this.sound.play("plantBit", {
                  volume:
                    Number(
                      localStorage.getItem("agritech-effects-volume") ?? "0.6",
                    ) * 0.5,
                });

                const plantKey = i === plantBits - 1 ? "flower" : "plant";

                const plantY = plantBaseY - plantSpacing * i;
                const finalPlantY =
                  plantKey === "flower" ? plantY - 20 : plantY;

                const finalPlantX = plantKey === "flower" ? plantX + 1 : plantX;

                const plantScale = plantKey === "flower" ? 0.1 : 0.12;

                this.add
                  .image(finalPlantX, finalPlantY, plantKey)
                  .setScale(plantScale)
                  .setDepth(character.depth + 1 + i);

                const growthValue =
                  plantKey === "flower" ? this.score - i * 2500 : 2500;

                const growthText = this.add
                  .text(finalPlantX + 34, finalPlantY - 8, `+${growthValue}`, {
                    fontFamily: '"Montserrat", sans-serif',
                    fontSize: "18px",
                    color: "#d9f99d",
                    fontStyle: "bold",
                    stroke: "#07130d",
                    strokeThickness: 4,
                  })
                  .setOrigin(0.5)
                  .setDepth(200);

                this.tweens.add({
                  targets: growthText,
                  y: growthText.y - 24,
                  alpha: 0,
                  duration: 700,
                  ease: "sine.out",
                  onComplete: () => growthText.destroy(),
                });

                if (plantKey === "flower") {
                  this.time.delayedCall(1000, () => {
                    const resultsTotal =
                      document.getElementById("results-total");

                    if (resultsTotal) {
                      resultsTotal.style.display = "";
                    }

                    this.sound.play("complete", {
                      volume:
                        Number(
                          localStorage.getItem("agritech-effects-volume") ??
                            "0.6",
                        ) * 0.6,
                    });

                    const revealBG = this.add
                      .image(400, 300, "titleBG")
                      .setDisplaySize(800, 600)
                      .setDepth(-10);

                    const revealMaskGraphics = this.make.graphics();
                    revealMaskGraphics.fillStyle(0xffffff);
                    revealMaskGraphics.fillCircle(finalPlantX, finalPlantY, 1);

                    const revealMask = revealMaskGraphics.createGeometryMask();
                    revealBG.setMask(revealMask);

                    const reveal = { radius: 1 };

                    this.tweens.add({
                      targets: reveal,
                      radius: 1000,
                      duration: 1400,
                      ease: "sine.out",
                      onUpdate: () => {
                        revealMaskGraphics.clear();
                        revealMaskGraphics.fillStyle(0xffffff);
                        revealMaskGraphics.fillCircle(
                          finalPlantX,
                          finalPlantY,
                          reveal.radius,
                        );
                      },
                      onComplete: () => {
                        revealBG.clearMask();
                        revealMaskGraphics.destroy();
                      },
                    });

                    const congratsText =
                      this.lang === "es" ? "¡FELICIDADES!" : "CONGRATS!";
                    const startX = 400 - (congratsText.length - 1) * 22;

                    this.showReturnButton(finalPlantX, finalPlantY);
                    this.time.delayedCall(1500, () => {
                      this.showMinorIdReminder();
                    });

                    congratsText.split("").forEach((letter, letterIndex) => {
                      const letterText = this.add
                        .text(startX + letterIndex * 44, 120, letter, {
                          fontFamily: '"Russo One", sans-serif',
                          fontSize: "44px",
                          color: "#ecfccb",
                          stroke: "#14532d",
                          strokeThickness: 6,
                        })
                        .setOrigin(0.5)
                        .setDepth(100);

                      this.tweens.add({
                        targets: letterText,
                        y: 105,
                        duration: 450,
                        delay: letterIndex * 80,
                        yoyo: true,
                        repeat: -1,
                        ease: "sine.inOut",
                      });
                    });
                  });
                }
              });
            }
          });
        });
      },
    });
  }
}
